import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Alert, Form, Modal, Typography } from "antd";
import { Dayjs } from "dayjs";
import * as React from "react";
import { Button, DatePicker } from "src/components";
import { useToggleWebsiteSystem, useUpdateWebsiteSystem, WebsiteSystemItem } from "src/hooks/useWebsiteSystem";
import { deserializeScheduleForm, ScheduleFormValue, serializeScheduleForm, validateScheduleForm } from "./scheduleForm";
import { selectedWebsiteProducts } from "./WebsiteLogos";

export type WebsiteSystemRenewRangeDialogProps = {
  item: WebsiteSystemItem;
};

type RenewRangeBodyProps = {
  item: WebsiteSystemItem;
  visible: boolean;
  hide: () => void;
  remove: () => void;
};

type Range = { start?: Dayjs; end?: Dayjs };

// Mounted with key={item.code} for the same reason as the edit modal's body: a
// reused NiceModal instance must not carry one row's range into another row.
const RenewRangeBody: React.FC<RenewRangeBodyProps> = ({ item, visible, hide, remove }) => {
  // Save-then-switch-on reports one outcome: the switch's own "อัปเดตสำเร็จ"
  // after the PATCH, so the PUT's success toast is off. A failure of either step
  // is shown inline instead, so the switch hook's error toast is off too (its
  // list refetch stays - see useToggleWebsiteSystem).
  const updateMutation = useUpdateWebsiteSystem(undefined, { successToast: false });
  const toggleMutation = useToggleWebsiteSystem({ onError: () => undefined });

  const initial = React.useRef<ScheduleFormValue>(deserializeScheduleForm(item)).current;
  const [range, setRange] = React.useState<Range>({ start: initial.customStart, end: initial.customEnd });
  const [error, setError] = React.useState<string>();
  // True for the whole save-then-switch-on run, including the gap between the
  // two requests, so ยกเลิก stays disabled until the run is over.
  const [submitting, setSubmitting] = React.useState(false);

  // The start the backend holds right now: the stored one at first, then the one
  // a PUT in this dialog just saved - after a PATCH that failed, a retry must
  // treat that saved start as unchanged, as the backend will.
  const savedStartRef = React.useRef<Dayjs | undefined>(initial.customStart);

  // Counts every way this dialog stops being on screen (hidden, or unmounted). A
  // run that started before a dismissal must not go on to the PATCH when its PUT
  // returns - react-query's mutateAsync resolves whether or not anyone is still
  // looking. Counted from visible true -> false only: a freshly shown NiceModal
  // first renders with visible false.
  const dismissalsRef = React.useRef(0);
  const wasVisibleRef = React.useRef(false);
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    if (visible) wasVisibleRef.current = true;
    else if (wasVisibleRef.current) dismissalsRef.current += 1;
  }, [visible]);
  React.useEffect(
    () => () => {
      mountedRef.current = false;
      dismissalsRef.current += 1;
    },
    []
  );

  const confirm = async () => {
    setError(undefined);
    // The picker shows minutes only, so a start the admin did not move is sent
    // as the stored value itself (seconds included): the backend exempts an
    // unchanged start from its "not in the past" rule by comparing to the second.
    const savedStart = savedStartRef.current;
    const customStartUnchanged =
      !!range.start &&
      !!savedStart &&
      range.start.format("YYYY-MM-DD HH:mm") === savedStart.format("YYYY-MM-DD HH:mm");
    const start = customStartUnchanged ? savedStart : range.start;
    const schedule: ScheduleFormValue = { typeTime: "custom", daysWeek: [], customStart: start, customEnd: range.end };
    // Keeping the old (past) start is allowed, as in the edit modal; a moved
    // start must be at least a minute ahead, and the end ahead of now, since the
    // backend will not switch on a window that has already ended.
    const invalid = validateScheduleForm(schedule, { customStartUnchanged, requireFutureEnd: true });
    if (invalid) {
      setError(invalid);
      return;
    }
    const serialized = serializeScheduleForm(schedule, item.stored);
    const dismissalsAtStart = dismissalsRef.current;
    const dismissed = () => dismissalsRef.current !== dismissalsAtStart;
    try {
      // Only the window changes. The row carries no text of its own (the request
      // builder sends the same empty message and no channel as the edit modal), and
      // a days_week a custom row still carries is kept rather than cleared
      // (through serializeScheduleForm, same rule as the edit modal).
      await updateMutation.mutateAsync({
        code: item.code,
        scope: item.scope,
        products: item.scope === "PER_PRODUCT" ? selectedWebsiteProducts(item.products) : undefined,
        typeTime: "custom",
        daysWeek: serialized.days_week,
        startDatetime: serialized.start_datetime,
        endDatetime: serialized.end_datetime,
      });
    } catch (putError: any) {
      if (dismissed()) return;
      setError(putError?.response?.data?.service_message || putError?.message || "บันทึกไม่สำเร็จ");
      return;
    }
    savedStartRef.current = start;
    // Dismissed while the PUT was out: the new window is saved, the row stays off.
    if (dismissed()) return;
    try {
      // The toggle hook already turns a rejection into an Error carrying the
      // backend's service_message.
      await toggleMutation.mutateAsync({ code: item.code, enabled: true });
    } catch (patchError: any) {
      if (dismissed()) return;
      setError(`บันทึกช่วงเวลาใหม่แล้ว แต่เปิดใช้งานไม่สำเร็จ: ${patchError?.message || "เปิดใช้งานไม่สำเร็จ"}`);
      return;
    }
    if (dismissed()) return;
    hide();
  };

  // Double-click guard - same shape as the edit modal's (the ref stops a second
  // click that lands before the "submitting" re-render disables anything).
  const submittingRef = React.useRef(false);
  const handleConfirm = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await confirm();
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      afterClose={remove}
      title="ช่วงเวลาที่กำหนดสิ้นสุดแล้ว"
      width={520}
      // Only the footer closes this dialog - no close icon, mask click or Esc -
      // and ยกเลิก is disabled while a save is running.
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
          <Button size="large" onClick={hide} disabled={submitting} style={{ width: 100 }}>
            ยกเลิก
          </Button>
          <Button
            size="large"
            type="primary"
            loading={submitting}
            onClick={handleConfirm}
            style={{ minWidth: 200 }}
          >
            บันทึกและเปิดใช้งาน
          </Button>
        </div>
      }
    >
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Typography.Paragraph>
        ช่วงเวลาแสดงของ “{item.name}” สิ้นสุดไปแล้ว กรุณากำหนดวันเวลาสิ้นสุดใหม่ก่อนเปิดใช้งาน
      </Typography.Paragraph>
      <Form layout="vertical">
        <Form.Item label="เวลาเริ่มต้น" style={{ marginBottom: 0 }}>
          <DatePicker.RangePicker
            showTime
            format="DD/MM/YY HH:mm"
            placeholder={["เวลาเริ่มต้น", "เวลาสิ้นสุด"]}
            value={range.start && range.end ? [range.start, range.end] : undefined}
            onChange={(value) => setRange({ start: value?.[0] || undefined, end: value?.[1] || undefined })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// Opened by the row's switch instead of a PATCH when the row's one-off custom
// window has already ended: asks for a new window, saves it (PUT), and only then
// switches the row on (PATCH). Cancel sends nothing, so the switch stays off.
export const WebsiteSystemRenewRangeDialog = NiceModal.create(({ item }: WebsiteSystemRenewRangeDialogProps) => {
  const { visible, hide, remove } = useModal();
  return <RenewRangeBody key={item.code} item={item} visible={visible} hide={hide} remove={remove} />;
});
